import { http } from '../http';
import { extrairMensagemErro } from '../../utils/apiError';

/**
 * Fila de divergências local x SGA (at-32) — aba "Divergências" de /ti/envios.
 *
 * Usa o cliente `http` central (at-17): o Bearer é injetado pelo interceptor,
 * nunca montado aqui.
 */

export type DivergenciaEntidade = 'ASSOCIADO' | 'VEICULO';
export type DivergenciaStatus = 'ABERTA' | 'ACEITA' | 'IGNORADA' | 'RESOLVIDA_ENVIO' | 'RETRATADA';
export type DivergenciaStatusFiltro = 'TODOS' | DivergenciaStatus;
export type DivergenciaOrigem = 'IMPORTACAO' | 'ON_ACCESS' | 'CRON';
/**
 * `INFORMATIVA` (at-54): campo que o SGA **não informou** e o SAAC preencheu por
 * obrigação da coluna (câmbio → MANUAL; RG/chassi/renavam → marcadores `SEM-*`).
 * Não é desacordo — um dos lados está vazio —, então não aparece na faixa das
 * telas de fluxo; só na fila do TI, que é quem pode completar o cadastro no SGA.
 */
export type DivergenciaGravidade = 'ALTA' | 'NORMAL' | 'INFORMATIVA';

/**
 * Divergência como o backend a entrega.
 *
 * `rotulo`, `descricao`, `gravidade` e `aplicavel` vêm prontos de lá: a regra de
 * "o que dá para aplicar e o que exige ação manual" mora na whitelist do
 * backend, e a tela só renderiza (convenção da at-24 sobre regra duplicada em
 * componente).
 */
export interface DivergenciaSga {
  id: string;
  entidade: DivergenciaEntidade;
  campo: string;
  rotulo: string;
  valorLocal: string | null;
  valorSga: string | null;
  detectadaEm: string;
  origemDeteccao: DivergenciaOrigem;
  gravidade: DivergenciaGravidade;
  descricao: string;
  /** Campo copiável entre os lados (regra do backend). Estruturais vêm false. */
  aplicavel: boolean;
}

export interface DivergenciaFila extends DivergenciaSga {
  status: DivergenciaStatus;
  associadoId: string | null;
  carroId: string | null;
  cpfCnpj: string | null;
  placa: string | null;
  associadoNome: string | null;
  resolvidaEm: string | null;
  aplicavel: boolean;
  orientacao: string | null;
}

export interface TotaisDivergencia {
  abertas: number;
  aceitas: number;
  ignoradas: number;
}

export interface ListaDivergencias {
  totalItems: number;
  page: number;
  limit: number;
  /** Contadores GLOBAIS — não acompanham a busca nem os filtros (at-10/at-28). */
  totais: TotaisDivergencia;
  items: DivergenciaFila[];
}

export interface ResolucaoDivergencia {
  id: string;
  status: 'ACEITA' | 'IGNORADA';
  /** true = o valor do SGA foi gravado no registro local. */
  aplicado: boolean;
  campo: string;
  rotulo: string;
  orientacao: string | null;
}

export interface FiltrosDivergencia {
  page?: number;
  limit?: number;
  busca?: string;
  status?: DivergenciaStatusFiltro;
  entidade?: DivergenciaEntidade;
}

export const listarDivergencias = async (
  filtros: FiltrosDivergencia = {},
): Promise<ListaDivergencias> => {
  try {
    const { data } = await http.get<ListaDivergencias>('/sga-divergencia', {
      params: {
        page: filtros.page ?? 1,
        limit: filtros.limit ?? 10,
        // Só manda o que tem valor: parâmetro vazio viraria filtro inválido (400).
        ...(filtros.busca ? { busca: filtros.busca } : {}),
        ...(filtros.status && filtros.status !== 'TODOS' ? { status: filtros.status } : {}),
        ...(filtros.entidade ? { entidade: filtros.entidade } : {}),
      },
    });
    return data;
  } catch (error) {
    throw new Error(extrairMensagemErro(error, 'Não foi possível carregar as divergências.'));
  }
};

/** Aplica o dado do SGA no registro local (quando o campo é aplicável). */
export const aceitarDivergencia = async (id: string): Promise<ResolucaoDivergencia> => {
  try {
    const { data } = await http.patch<ResolucaoDivergencia>(`/sga-divergencia/${id}/aceitar`);
    return data;
  } catch (error) {
    throw new Error(extrairMensagemErro(error, 'Não foi possível aceitar o dado do SGA.'));
  }
};

/** Tira a divergência da fila. Não renasce numa próxima reconciliação. */
export const ignorarDivergencia = async (id: string): Promise<ResolucaoDivergencia> => {
  try {
    const { data } = await http.patch<ResolucaoDivergencia>(`/sga-divergencia/${id}/ignorar`);
    return data;
  } catch (error) {
    throw new Error(extrairMensagemErro(error, 'Não foi possível ignorar a divergência.'));
  }
};
